"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextAreaField = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const utils_1 = require("@react-aria/utils");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const components_1 = require("../components");
const TextFieldBase_1 = require("./TextFieldBase");
const utils_2 = require("../utils");
/** Returns a <textarea /> element that auto-adjusts height based on the field's value */
function TextAreaField(props) {
    const { value = "", disabled = false, readOnly = false, onBlur, onFocus, preventNewLines, onEnter, ...otherProps } = props;
    const isDisabled = !!disabled;
    const isReadOnly = !!readOnly;
    const textFieldProps = { ...otherProps, value, isDisabled, isReadOnly };
    const inputRef = (0, react_1.useRef)(null);
    const inputWrapRef = (0, react_1.useRef)(null);
    // not in stately because this is so we know when to re-measure, which is a spectrum design
    const onHeightChange = (0, react_1.useCallback)(() => {
        const input = inputRef.current;
        const inputWrap = inputWrapRef.current;
        if (input && inputWrap) {
            const prevAlignment = input.style.alignSelf;
            input.style.alignSelf = "start";
            input.style.height = "auto";
            // Adding 2px to height avoids showing the scrollbar. This is to compensate for the border due to `box-sizing: border-box;`
            inputWrap.style.height = `${input.scrollHeight + 2}px`;
            // Set the textarea's height back to 100% so it takes up the full `inputWrap`
            input.style.height = "100%";
            input.style.alignSelf = prevAlignment;
        }
    }, [inputRef]);
    (0, utils_1.useLayoutEffect)(() => {
        if (inputRef.current) {
            // Temp hack until we can figure out a better way to ensure proper measurements when rendered through a portal (i.e. Modals)
            if (inputRef.current.scrollHeight === 0) {
                setTimeout(() => onHeightChange(), 0);
                return;
            }
            onHeightChange();
        }
    }, [onHeightChange, value, inputRef]);
    const { labelProps, inputProps } = (0, react_aria_1.useTextField)({
        ...textFieldProps,
        inputElementType: "textarea",
        ...(preventNewLines
            ? {
                onKeyDown: (e) => {
                    var _a;
                    // Prevent user from typing the new line character
                    if (e.key === "Enter") {
                        e.preventDefault();
                        (0, utils_2.maybeCall)(onEnter);
                        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                    }
                },
                onInput: (e) => {
                    // Prevent user from pasting content that has new line characters and replace with empty space.
                    const target = e.target;
                    target.value = target.value.replace(/[\n\r]/g, " ");
                },
            }
            : {}),
    }, inputRef);
    return ((0, jsx_runtime_1.jsx)(TextFieldBase_1.TextFieldBase, { ...(0, react_aria_1.mergeProps)(otherProps, { onBlur, onFocus }), multiline: true, labelProps: labelProps, inputProps: inputProps, inputRef: inputRef, inputWrapRef: inputWrapRef, textAreaMinHeight: preventNewLines ? 0 : undefined, tooltip: (0, components_1.resolveTooltip)(disabled, undefined, readOnly) }));
}
exports.TextAreaField = TextAreaField;
